<script>
  import { modal } from '../lib/modal.js';
  import { createEventDispatcher } from 'svelte';
  import { Wallet, AlertTriangle } from '../lib/icons/index.js';
  import { t } from '../i18n/index.js';
  import { companyInvoicesApi } from '../stores/api.js';
  import { todayCalendarDay } from '../lib/payment.js';
  import { formatEuro, formatDate } from '../lib/labels.js';
  import {
    paymentTotals, recordPaymentsSequentially, METODI_EFFETTIVI, metodoEffettivoLabel,
  } from '../lib/payment-status.js';
  import InfoHint from './InfoHint.svelte';

  /**
   * Registra con una data sola il pagamento di più fatture, ciascuna per
   * l'intero residuo. Prima conferma numero e totale; poi una chiamata per
   * fattura, una dopo l'altra, e alla fine l'elenco di quelle non passate.
   */
  export let companyId;
  /** Le righe selezionate, ciascuna con `pagamento`. */
  export let invoices = [];

  const dispatch = createEventDispatcher();
  const today = todayCalendarDay();

  let dataPagamento = today;
  let metodo = '';
  let running = false;
  let done = 0;
  let outcome = null;

  $: total = paymentTotals(invoices).residuo;

  async function run() {
    running = true;
    done = 0;
    outcome = await recordPaymentsSequentially(invoices, {
      dataPagamento,
      metodo,
      record: async (invoice, body) => {
        try {
          return await companyInvoicesApi.recordPayment(companyId, invoice.id, body);
        } finally {
          done += 1;
        }
      },
    });
    running = false;
  }

  function close() {
    if (running) return;
    if (outcome) dispatch('done', outcome);
    else dispatch('cancel');
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div class="modal-backdrop" on:click|self={close}>
  <div class="modal-content w-full max-w-lg p-6" role="dialog" aria-modal="true" aria-labelledby="bulk-payment-title" use:modal={{ onClose: close }}>
    <h2 id="bulk-payment-title" class="text-lg text-heading font-display mb-1 flex items-center gap-2">
      <Wallet size={20} /> {$t('invoices.pagamento.bulk.title')}
    </h2>

    {#if outcome}
      <p class="text-sm mt-2" role="status" style="color: var(--color-text-body);">
        {outcome.failed.length === 0
          ? $t('invoices.pagamento.bulk.doneAll', { n: outcome.recorded.length })
          : $t('invoices.pagamento.bulk.doneSome', { ok: outcome.recorded.length, n: invoices.length })}
      </p>
      {#if outcome.failed.length > 0}
        <ul class="mt-2 space-y-1 text-xs list-disc list-inside text-danger">
          {#each outcome.failed as failure}
            {#if failure.settled}
              <!-- Settled by somebody else meanwhile: nothing to re-confirm. -->
              <li>{$t('invoices.pagamento.bulk.failedSettled', { numero: failure.row.invoiceNumber || failure.row.id })}</li>
            {:else if failure.residuo !== null}
              <!-- The residual moved: reported with its new figure, never retried here. -->
              <li>{$t('invoices.pagamento.bulk.failedChanged', { numero: failure.row.invoiceNumber || failure.row.id, residuo: formatEuro(failure.residuo) })}</li>
            {:else}
              <li>{$t('invoices.pagamento.bulk.failedRow', { numero: failure.row.invoiceNumber || failure.row.id, motivo: failure.message })}</li>
            {/if}
          {/each}
        </ul>
      {/if}
      <div class="flex justify-end pt-5">
        <button type="button" class="btn btn-primary btn-sm" on:click={close}>{$t('common.close')}</button>
      </div>
    {:else}
      <p class="text-sm mb-4" style="color: var(--color-text-body);">{$t('invoices.pagamento.bulk.intro')}</p>
      <div>
        <label class="label" for="bulk-payment-date">{$t('invoices.pagamento.bulk.date')}</label>
        <input id="bulk-payment-date" type="date" class="input" bind:value={dataPagamento} max={today} disabled={running} />
      </div>
      <div class="mt-4">
        <label class="label" for="bulk-payment-method">
          {$t('invoices.pagamento.metodo.label')}<InfoHint term="metodoEffettivo" />
        </label>
        <select id="bulk-payment-method" class="input" bind:value={metodo} disabled={running}>
          <option value="">{$t('invoices.pagamento.metodo.none')}</option>
          {#each METODI_EFFETTIVI as m}
            <option value={m}>{$t(metodoEffettivoLabel(m))}</option>
          {/each}
        </select>
        <p class="mt-1 text-xs text-muted">{$t('invoices.pagamento.metodo.bulkHelp')}</p>
      </div>
      <div class="card p-4 mt-4 border-yellow-500" role="alert">
        <p class="text-sm flex items-start gap-2" style="color: var(--color-text-body);">
          <AlertTriangle size={16} class="text-warning shrink-0 mt-0.5" />
          <span>{$t('invoices.pagamento.bulk.confirm', { n: invoices.length, importo: formatEuro(total), data: formatDate(dataPagamento) })}</span>
        </p>
      </div>
      {#if running}
        <p class="text-xs text-muted mt-3" role="status">{$t('invoices.pagamento.bulk.running', { done, n: invoices.length })}</p>
      {/if}
      <div class="flex justify-end gap-3 pt-5">
        <button type="button" class="btn btn-secondary btn-sm" on:click={() => dispatch('cancel')} disabled={running}>
          {$t('common.cancel')}
        </button>
        <button type="button" class="btn btn-primary btn-sm" on:click={run}
          disabled={running || !dataPagamento || invoices.length === 0}>
          {$t('invoices.pagamento.bulk.submit', { n: invoices.length })}
        </button>
      </div>
    {/if}
  </div>
</div>
